import React, { useState, useMemo, useEffect } from 'react';
import { useTableData } from './useTableData';
import { useTableState } from './useTableState';
import { OmniSearch } from './OmniSearch';
import { TableHeaders } from './TableHeaders';
import { TableBody } from './TableBody';

export function DynamicTableViewer({ targetTable, token, uiConfig, onMissingConfig, apiPrefix = '/api/data' }) {
    const tableData = useTableData({ targetTable, token, apiPrefix, uiConfig, onMissingConfig });
    const tableState = useTableState({ targetTable, token });

    const [isAdding, setIsAdding] = useState(false);
    const [editingRowPk, setEditingRowPk] = useState(null);
    const [editFormData, setEditFormData] = useState({});
    const [deletingRowPk, setDeletingRowPk] = useState(null);

    // Data Processing Pipeline
    const processedData = useMemo(() => {
        let result = [...tableData.data];

        // 1. Filter
        if (tableState.searchQuery) {
            const query = tableState.searchQuery.trim().toLowerCase();
            const colMatch = query.match(/^@(\w+):\s*(.*)$/i);
            
            if (colMatch) {
                // Targeted column search
                const colName = colMatch[1];
                const searchVal = colMatch[2].toLowerCase();
                
                result = result.filter(row => {
                    let val = row[colName];
                    if (val === null || val === undefined) return false;
                    
                    // If it's an FK, search against the resolved label too
                    if (tableData.fkOptions[colName]) {
                        const match = tableData.fkOptions[colName].options.find(opt => opt.value === val);
                        if (match && String(match.label).toLowerCase().includes(searchVal)) return true;
                    }
                    return String(val).toLowerCase().includes(searchVal);
                });
            } else if (!query.startsWith('@')) {
                // General search
                result = result.filter(row => {
                    return Object.entries(row).some(([colName, val]) => {
                        if (val === null || val === undefined) return false;
                        if (tableData.fkOptions[colName]) {
                            const match = tableData.fkOptions[colName].options.find(opt => opt.value === val);
                            if (match && String(match.label).toLowerCase().includes(query)) return true;
                        }
                        return String(val).toLowerCase().includes(query);
                    });
                });
            }
        }

        // 2. Sort
        if (tableState.sortConfig.length > 0) {
            result.sort((a, b) => {
                for (const sort of tableState.sortConfig) {
                    let valA = a[sort.key];
                    let valB = b[sort.key];
                    
                    // Use resolved labels for FKs if available
                    if (tableData.fkOptions[sort.key]) {
                        const matchA = tableData.fkOptions[sort.key].options.find(opt => opt.value === valA);
                        if (matchA) valA = matchA.label;
                        const matchB = tableData.fkOptions[sort.key].options.find(opt => opt.value === valB);
                        if (matchB) valB = matchB.label;
                    }

                    if (valA === valB) continue;
                    if (valA === null || valA === undefined) return 1;
                    if (valB === null || valB === undefined) return -1;
                    
                    const aStr = String(valA).toLowerCase();
                    const bStr = String(valB).toLowerCase();
                    const compare = aStr < bStr ? -1 : 1;
                    
                    return sort.direction === 'asc' ? compare : -compare;
                }
                return 0;
            });
        }

        // 3. Group recursively
        if (tableState.groupByConfig.length > 0) {
            const groupData = (dataArray, keys) => {
                if (keys.length === 0) return dataArray;
                const [currentKey, ...remainingKeys] = keys;
                
                const grouped = {};
                dataArray.forEach(row => {
                    let groupVal = row[currentKey];
                    if (tableData.fkOptions[currentKey]) {
                        const match = tableData.fkOptions[currentKey].options.find(opt => opt.value === groupVal);
                        if (match) groupVal = match.label;
                    }
                    const keyStr = String(groupVal ?? 'null');
                    
                    if (!grouped[keyStr]) grouped[keyStr] = [];
                    grouped[keyStr].push(row);
                });

                Object.keys(grouped).forEach(k => {
                    grouped[k] = groupData(grouped[k], remainingKeys);
                });

                return grouped;
            };

            return groupData(result, tableState.groupByConfig);
        }

        return result;
    }, [tableData.data, tableData.fkOptions, tableState.searchQuery, tableState.sortConfig, tableState.groupByConfig]);


    // Handlers
    const handleAddClick = () => {
        if (tableData.schemaWarning) return alert(tableData.schemaWarning);
        setIsAdding(true);
        setEditingRowPk(null);
        setEditFormData({});
    };

    const handleEditClick = (row) => {
        if (tableData.schemaWarning) return alert(tableData.schemaWarning);
        const pkCol = tableData.getPrimaryKeyColumn();
        setIsAdding(false);
        setEditingRowPk(row[pkCol.ColumnName]);
        setEditFormData({ ...row });
    };

    const handleInputChange = (e, colName) => {
        const val = e.target.value;
        setEditFormData(prev => {
            const updated = { ...prev, [colName]: val };
            if (tableData.isPolymorphicTable) {
                if (colName === 'TargetSchema') {
                    updated.TargetTable = '';
                    updated.TargetRecordID = '';
                } else if (colName === 'TargetTable') {
                    updated.TargetRecordID = '';
                }
            }
            return updated;
        });
    };

    const handleSaveClick = async (isUpdateCheck) => {
        const pkCol = tableData.getPrimaryKeyColumn();
        const isUpdate = !isAdding;
        const payload = {};

        tableData.columns.forEach(col => {
            if (col.IsIdentity || (isUpdate && col.IsPrimaryKey)) return;
            if (editFormData[col.ColumnName] !== undefined) {
                payload[col.ColumnName] = editFormData[col.ColumnName];
            }
        });

        const success = await tableData.saveRow(isUpdate, pkCol.ColumnName, editFormData[pkCol.ColumnName], payload);
        if (success) {
            setIsAdding(false);
            setEditingRowPk(null);
            setEditFormData({});
        }
    };

    const handleDeleteClick = async (row) => {
        if (tableData.schemaWarning) return alert(tableData.schemaWarning);
        const pkCol = tableData.getPrimaryKeyColumn();
        const pkVal = row[pkCol.ColumnName];
        setDeletingRowPk(pkVal);
        await tableData.checkDependencies(pkVal);
    };

    const confirmDelete = async () => {
        const pkCol = tableData.getPrimaryKeyColumn();
        const success = await tableData.deleteRow(pkCol.ColumnName, deletingRowPk);
        if (success) {
            setDeletingRowPk(null);
        }
    };

    const [loadingPolyRecords, setLoadingPolyRecords] = useState(false);
    const [polymorphicRecords, setPolymorphicRecords] = useState([]);

    // Fetch polymorphic target records when schema and table are selected
    useEffect(() => {
        let isMounted = true;
        
        const fetchPolyRecords = async () => {
            const { TargetSchema, TargetTable } = editFormData;
            if (!TargetSchema || !TargetTable || !tableData.isPolymorphicTable) {
                setPolymorphicRecords([]);
                return;
            }

            setLoadingPolyRecords(true);
            try {
                const headers = { 'Authorization': `Bearer ${token}` };
                
                const metaRes = await fetch(`${apiPrefix}/schema/metadata/${TargetSchema}/${TargetTable}`, { headers });
                let pkCol = 'id';
                if (metaRes.ok) {
                    const meta = await metaRes.json();
                    const pkMeta = meta.find(m => m.IsPrimaryKey);
                    if (pkMeta) pkCol = pkMeta.ColumnName;
                }

                const targetUiConf = Array.isArray(uiConfig) ? uiConfig.find(c => c.TargetSchema === TargetSchema && c.TargetTable === TargetTable) : null;
                const displayCol = targetUiConf ? targetUiConf.DisplayColumn : null;

                const res = await fetch(`${apiPrefix}/${TargetSchema}/${TargetTable}`, { headers });
                if (res.ok) {
                    const data = await res.json();
                    const options = data.map(row => ({
                        value: row[pkCol],
                        label: displayCol && row[displayCol] ? row[displayCol] : String(row[pkCol])
                    }));
                    if (isMounted) setPolymorphicRecords(options);
                }
            } catch (err) {
                console.error("Failed to fetch polymorphic records:", err);
            } finally {
                if (isMounted) setLoadingPolyRecords(false);
            }
        };

        fetchPolyRecords();

        return () => { isMounted = false; };
    }, [editFormData.TargetSchema, editFormData.TargetTable, tableData.isPolymorphicTable, apiPrefix, token, uiConfig]);

    const uniqueSchemas = Array.from(new Set(tableData.tablesList.map(t => t.split('.')[0])));

    if (!targetTable) return <div className="p-4 text-slate-400">Please select a table from the dropdown to view its data.</div>;
    if (tableData.loading || !tableState.preferencesLoaded) return <div className="p-4 text-slate-400">Loading table data...</div>;
    if (tableData.error) return <div className="p-4 text-red-400">Error: {tableData.error}</div>;

    return (
        <div className="flex flex-col h-full relative">
            {tableData.schemaWarning && (
                <div className="bg-red-900/50 border border-red-500 text-red-200 px-4 py-3 m-4 rounded">
                    {tableData.schemaWarning}
                </div>
            )}

            <div className="p-4 bg-slate-800 border-b border-slate-700 flex flex-col gap-4 shrink-0">
                <div className="flex justify-between items-center">
                    <h3 className="text-lg font-medium text-white">{targetTable}</h3>
                    <div className="flex gap-4 items-center flex-1 ml-8 mr-4">
                        <OmniSearch 
                            searchQuery={tableState.searchQuery}
                            setSearchQuery={tableState.setSearchQuery}
                            columns={tableData.columns}
                            fkOptions={tableData.fkOptions}
                        />
                    </div>
                    <button 
                        onClick={handleAddClick}
                        disabled={!!tableData.schemaWarning || isAdding || editingRowPk}
                        className="bg-blue-600 hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2 rounded text-sm transition-colors shrink-0"
                    >
                        + Add Record
                    </button>
                </div>
                
                {tableState.groupByConfig.length > 0 && (
                    <div className="flex items-center gap-2 bg-slate-900 p-2 rounded border border-slate-700 overflow-x-auto">
                        <span className="text-slate-400 text-sm whitespace-nowrap">Grouped By:</span>
                        {tableState.groupByConfig.map((groupCol, idx) => (
                            <div key={groupCol} className="flex items-center gap-1 bg-slate-700 text-slate-200 px-2 py-1 rounded text-sm">
                                <span>{groupCol}</span>
                                {idx > 0 && (
                                    <button onClick={() => tableState.moveGroup(idx, idx - 1)} className="hover:text-blue-400" title="Move left">
                                        <span className="material-symbols-outlined text-[14px]">chevron_left</span>
                                    </button>
                                )}
                                <button onClick={() => tableState.removeGroup(groupCol)} className="hover:text-red-400 ml-1" title="Remove grouping">
                                    <span className="material-symbols-outlined text-[14px]">close</span>
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className="overflow-x-auto flex-1 bg-slate-800">
                <table className="w-full text-left text-sm text-slate-300">
                    <TableHeaders 
                        columns={tableData.columns}
                        fks={tableData.fks}
                        fkOptions={tableData.fkOptions}
                        sortConfig={tableState.sortConfig}
                        handleSort={tableState.handleSort}
                        addGroup={tableState.addGroup}
                        groupByConfig={tableState.groupByConfig}
                    />
                    <TableBody 
                        processedData={processedData}
                        columns={tableData.columns}
                        pkCol={tableData.getPrimaryKeyColumn()}
                        fks={tableData.fks}
                        fkOptions={tableData.fkOptions}
                        isPolymorphicTable={tableData.isPolymorphicTable}
                        polymorphicLabels={tableData.polymorphicLabels}
                        editingRowPk={editingRowPk}
                        handleEditClick={handleEditClick}
                        handleDeleteClick={handleDeleteClick}
                        handleSaveClick={handleSaveClick}
                        handleCancelClick={() => { setIsAdding(false); setEditingRowPk(null); setEditFormData({}); }}
                        schemaWarning={tableData.schemaWarning}
                        isAdding={isAdding}
                        editFormData={editFormData}
                        handleInputChange={handleInputChange}
                        uniqueSchemas={uniqueSchemas}
                        tablesList={tableData.tablesList}
                        loadingPolyRecords={loadingPolyRecords}
                        polymorphicRecords={polymorphicRecords}
                        groupByConfig={tableState.groupByConfig}
                    />
                </table>
            </div>

            {/* Deletion Modal */}
            {deletingRowPk !== null && (
                <div className="absolute inset-0 bg-slate-950/80 flex items-center justify-center z-50 p-4">
                    <div className="bg-slate-800 border border-slate-600 rounded-lg shadow-xl max-w-md w-full p-6">
                        <h2 className="text-xl font-bold text-red-400 mb-4">Confirm Deletion</h2>
                        <p className="text-slate-300 mb-4">Are you sure you want to delete record ID <strong>{deletingRowPk}</strong>?</p>

                        {tableData.deletingLoading ? (
                            <div className="bg-slate-900 rounded p-4 text-center text-slate-400 mb-4 border border-slate-700">Analyzing dependencies...</div>
                        ) : tableData.dependencyGraph && tableData.dependencyGraph.length > 0 ? (
                            <div className="bg-red-900/30 border border-red-800 rounded p-4 mb-4">
                                <h4 className="text-red-300 font-bold mb-2">Dependency Warning</h4>
                                <ul className="text-sm text-red-300 space-y-1">
                                    {tableData.dependencyGraph.map((dep, i) => (
                                        <li key={i}>• <strong>{dep.count}</strong> record(s) in <code>{dep.table}</code></li>
                                    ))}
                                </ul>
                            </div>
                        ) : tableData.dependencyGraph && tableData.dependencyGraph.length === 0 ? (
                            <div className="bg-green-900/30 border border-green-800 rounded p-4 mb-4 text-green-300 text-sm">✓ Safe to delete.</div>
                        ) : null}

                        <div className="flex justify-end gap-3 mt-6">
                            <button onClick={() => setDeletingRowPk(null)} className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded">Cancel</button>
                            <button onClick={confirmDelete} disabled={tableData.deletingLoading} className="px-4 py-2 bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white rounded font-bold">Force Delete</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
